import { useState, useEffect } from "react"
import { Search, Plus } from "lucide-react"
import { Button } from "../ui/button"
import { Input } from "../ui/input"
import { Select } from "../ui/select"

interface AttorneyControlsBarProps {
  recordsPerPage: number
  onRecordsPerPageChange: (value: string) => void
  currentPage: number
  totalPages: number
  onPreviousPage: () => void
  onNextPage: () => void
  onAddAttorney: () => void
  recordsList: { value: string; label: string }[]
  searchQuery?: string
  onSearchChange?: (query: string) => void
  onSearchInputChange?: (query: string) => void
  currentRecordsCount?: number
}

export function AttorneyControlsBar({
  recordsPerPage,
  onRecordsPerPageChange,
  currentPage,
  totalPages,
  onPreviousPage,
  onNextPage,
  onAddAttorney,
  recordsList,
  searchQuery = "",
  onSearchChange,
  onSearchInputChange,
  currentRecordsCount = 0,
}: AttorneyControlsBarProps) {
  const [searchValue, setSearchValue] = useState(searchQuery)

  // Update local search value when prop changes (e.g., when search is cleared from parent)
  useEffect(() => {
    setSearchValue(searchQuery)
  }, [searchQuery])

  const handleSearchChange = (value: string) => {
    setSearchValue(value)
    onSearchInputChange?.(value)
  }

  const handleSearch = () => {
    onSearchChange?.(searchValue)
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  const clearSearch = () => {
    setSearchValue("")
    onSearchInputChange?.("")
    onSearchChange?.("")
  }

  return (
    <div className="mb-6 space-y-4">
      {/* Desktop Layout */}
      <div className="hidden md:flex items-center justify-between gap-4">
        {/* Left Section - Search */}
        <div className="flex items-center gap-4 flex-1 max-w-2xl">
          {/* Search Bar */}
          <div className="relative flex-1 max-w-md">
            <Search 
              className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400 cursor-pointer hover:text-slate-600" 
              onClick={handleSearch}
            />
            <Input
              placeholder="Search attorneys by name or phone"
              value={searchValue}
              onChange={(e) => handleSearchChange(e.target.value)}
              onKeyPress={handleKeyPress}
              className="pl-10 pr-4"
            />
            {searchValue && (
              <button
                onClick={clearSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                ×
              </button>
            )}
          </div>
        </div>

        {/* Right Section - Controls */}
        <div className="flex items-center gap-3">
          {/* Records Per Page */}
          <Select value={recordsPerPage.toString()} onValueChange={onRecordsPerPageChange} className="w-44">
            {recordsList.map((record) => (
              <Select.Option key={record.value} value={record.value}>
                {record.label}
              </Select.Option>
            ))}
          </Select>

          {/* Add Attorney Button */}
          <Button onClick={onAddAttorney} className="bg-slate-900 hover:bg-slate-800 text-white">
            <Plus className="h-4 w-4 mr-2" />
            Add Attorney
          </Button>
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="md:hidden space-y-3">
        {/* Add Attorney and Records Row */}
        <div className="flex items-center justify-between gap-3">
          {/* Records Per Page - Mobile Dropdown */}
          <Select value={recordsPerPage.toString()} onValueChange={onRecordsPerPageChange} className="w-44">
            {recordsList.map((record) => (
              <Select.Option key={record.value} value={record.value}>
                {record.label}
              </Select.Option>
            ))}
          </Select>

          {/* Add Attorney Button */}
          <Button onClick={onAddAttorney} size="sm" className="bg-slate-900 hover:bg-slate-800 text-white">
            <Plus className="h-4 w-4 mr-1" />
            Add Attorney
          </Button>
        </div>

        {/* Search Row */}
        <div className="flex items-center gap-3">
          {/* Search Bar */}
          <div className="relative flex-1">
            <Search 
              className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400 cursor-pointer hover:text-slate-600" 
              onClick={handleSearch}
            />
            <Input
              placeholder="Search attorneys by name or phone"
              value={searchValue}
              onChange={(e) => handleSearchChange(e.target.value)}
              onKeyPress={handleKeyPress}
              className="pl-10 pr-4"
            />
            {searchValue && (
              <button
                onClick={clearSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                ×
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
} 